import { useParentRoutesPatient } from "@/api/Patients";
import { Heading } from "@medoma/components/Heading/Heading";
import { PlainButton } from "@medoma/components/Button/Button";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import styles from "./PatientAuthentication.module.scss";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { otpSchema } from "@/api/Auth";
import {
  generateOtp as generateOtpOnServer,
  generateTemporaryPin as generateTemporaryPinOnServer,
  signOutPatient as signOutPatientOnServer,
  isPatientSignedInQueryOptions,
} from "@/api/Auth";
import { Text } from "@medoma/components/Text/Text";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useEffect, useState } from "react";
import {
  getPatientFullName,
  type IExistingPatient,
} from "@medoma/models/patients";
import { TextCode } from "@medoma/components/TextCode/TextCode";
import type { z } from "zod";

const OTP = ({
  patientId,
  isPatientSignedIn,
}: {
  patientId: string;
  isPatientSignedIn: boolean;
}) => {
  const { t } = useLingui();
  const [otp, setOtp] = useState<z.infer<typeof otpSchema> | null>(null);

  const {
    mutate: generateOtp,
    isPending: generatingOtp,
    isError: isErrorOtp,
  } = useMutation({
    mutationFn: () => generateOtpOnServer(patientId),
    onSuccess: (data) => setOtp(data),
  });

  // Ensure OTP is nullified when patient is signed out, to avoid showing stale data
  useEffect(() => {
    if (!isPatientSignedIn) {
      // Acceptable, since we (re)set to a fixed state
      // oxlint-disable-next-line react/set-state-in-effect
      setOtp(null);
    }
  }, [isPatientSignedIn]);

  // Don't display OTP UI if patient is already signed in
  if (isPatientSignedIn) {
    return null;
  }

  return (
    <section>
      <Heading level="h2" margin="bottom">
        <Trans>Startkod</Trans>
      </Heading>
      <Text element="p">
        <Trans>Startkoden används vid första inloggningen</Trans>
      </Text>
      {otp ? (
        <div className={styles.textAndButton}>
          <PlainButton disabled={generatingOtp} onClick={() => generateOtp()}>
            <Trans>Skapa ny startkod</Trans>
          </PlainButton>
          <div className={styles.codeContainer}>
            <TextCode textCode={otp.code} />
            <Plural
              value={otp.otpLifetimeInMinutes}
              one="Giltig i en minut"
              other="Giltig i # minuter"
            />
          </div>
        </div>
      ) : (
        <PlainButton disabled={generatingOtp} onClick={() => generateOtp()}>
          <Trans>Skapa startkod</Trans>
        </PlainButton>
      )}
      {isErrorOtp ? (
        <ErrorMessage
          message={t`Det gick inte att skapa en startkod. Försök igen om en stund och kontakta teknisk support om problemet kvarstår.`}
        />
      ) : (
        <></>
      )}
    </section>
  );
};

const PinCode = ({
  patient,
}: {
  patient: Pick<IExistingPatient, "id" | "name">;
}) => {
  const { t } = useLingui();
  const {
    mutate: generateTemporaryPin,
    isPending: generatingTemporaryPin,
    isError: isErrorPin,
  } = useMutation({
    mutationFn: () => generateTemporaryPinOnServer(patient.id),
    onSuccess: (pin) => setTemporaryPin(pin),
  });

  const [temporaryPin, setTemporaryPin] = useState<string | null>(null);

  const patientName = getPatientFullName(patient.name);
  return (
    <section>
      <Heading level="h2" margin="top-and-bottom">
        <Trans>Glömt pinkod?</Trans>
      </Heading>
      <div className={styles.textAndButton}>
        <PlainButton
          disabled={generatingTemporaryPin}
          onClick={() => {
            if (
              window.confirm(
                t`Är du säker på att du vill skapa en ny pinkod för ${patientName}? Du behöver ge den tillfälliga pinkoden till patienten. Därefter får patienten själv välja sin nya kod.`,
              )
            ) {
              generateTemporaryPin();
            }
          }}
        >
          <Trans>Skapa tillfällig pinkod</Trans>
        </PlainButton>
        {temporaryPin ? (
          <>
            <div className={styles.codeContainer}>
              <TextCode textCode={temporaryPin} />
            </div>
            <Text element="p">
              <Trans>
                Patienten använder den tillfälliga pinkoden för att logga in och
                väljer därefter en ny personlig pinkod
              </Trans>
            </Text>
          </>
        ) : (
          <></>
        )}
        {isErrorPin ? (
          <ErrorMessage
            message={t`Det gick inte att skapa en tillfällig pinkod. Försök igen om en stund och kontakta teknisk support om problemet kvarstår.`}
          />
        ) : (
          <></>
        )}
      </div>
    </section>
  );
};

export const PatientAuthentication = () => {
  const { t } = useLingui();
  const patient = useParentRoutesPatient();

  const { data: isPatientSignedIn } = useQuery(
    isPatientSignedInQueryOptions(patient.id),
  );

  const queryClient = useQueryClient();
  const {
    mutate: signOutPatient,
    isPending: signingOutPatient,
    isError: isErrorSigningOutPatient,
  } = useMutation({
    mutationFn: () => signOutPatientOnServer(patient.id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: isPatientSignedInQueryOptions(patient.id).queryKey,
      }),
  });

  const patientName = getPatientFullName(patient.name);
  return (
    <div className={styles.container}>
      {isPatientSignedIn ? (
        <section>
          <div className={styles.signedIn}>
            <div className={styles.signedInIndicator}></div>
            <Text element="div" size="small" weight="bold">
              <Trans>{patientName} är inloggad</Trans>
            </Text>
          </div>
          <Text element="p">
            <Trans>
              Patienten loggas ut automatiskt vid utskrivning, men det kan göras
              här om det behövs
            </Trans>
          </Text>

          <PlainButton
            disabled={signingOutPatient}
            onClick={() => {
              if (
                window.confirm(
                  t`Är du säker på att du vill logga ut ${patientName}? Patienten behöver en ny startkod för att kunna logga in på nytt.`,
                )
              ) {
                signOutPatient();
              }
            }}
          >
            <Trans>Logga ut {patientName}</Trans>
          </PlainButton>

          {isErrorSigningOutPatient ? (
            <ErrorMessage
              message={t`Det gick inte att logga ut ${patientName}. Försök igen om en stund och kontakta teknisk support om problemet kvarstår.`}
            />
          ) : (
            <></>
          )}
        </section>
      ) : null}
      <section className={styles.sections}>
        <OTP
          patientId={patient.id}
          isPatientSignedIn={isPatientSignedIn ?? false}
        />
        <PinCode patient={patient} />
      </section>
    </div>
  );
};
